/**
 * v2 VDSL port detail — deep-link target from VdslPortsCard's
 * info-icon expand. Reads ``GET /api/chassis/{id}/vdsl-ports/{slot}/{port}``
 * (same endpoint as v1) and surfaces:
 *
 * - Inventory row (state, template, last MAC, service ports)
 * - Line-speed trend chart (Recharts) + sample history table
 * - SELT history (most recent first)
 * - Two destructive action buttons — two-click ConfirmDialog gated
 *
 * Submitting a destructive job hands off to the InlineJobTracker
 * via the standard ``inline-job-tracker.add`` custom event so the
 * operator can watch it without navigating away.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../../../api/client";
import { LocalTime, formatLocalShort } from "../../../lib/datetime";
import { Badge, type BadgeTone } from "../../components/Badge";
import { useConfirm } from "../../components/ConfirmDialog";
import { CopyableCell } from "../../components/CopyableCell";
import { useToast } from "../../components/Toast";
import { HelpButton } from "../../help/HelpPanel";
import { formatCounterValue, groupCounters } from "../../lib/vdslCounters";
import { VdslErrorHealthPanel } from "../../components/VdslErrorHealthPanel";
import type { VdslErrors7d } from "../../lib/signalTones";

type ServicePort = {
  service_port_id: number;
  vlan: number;
  frame: number;
  slot: number;
  port: number;
  user_vlan: string;
  tag_transform: string;
};

type VdslPortRow = {
  chassis_id: number;
  frame: number;
  slot: number;
  port: number;
  line_state: string | null;
  loopback: string | null;
  template_index: number | null;
  last_seen_mac: string | null;
  last_seen_device: string | null;
  service_ports: ServicePort[];
  missing_scans: number;
  observed_at: string;
  error_health?: string | null;
  errors_7d?: VdslErrors7d | null;
  error_health_at?: string | null;
};

type SamplePoint = {
  observed_at: string;
  up_snr_margin_db: number | null;
  dw_snr_margin_db: number | null;
  up_atten_db: number | null;
  dw_atten_db: number | null;
  up_max_rate_kbps: number | null;
  dw_max_rate_kbps: number | null;
  up_out_power_dbm: number | null;
  dw_out_power_dbm: number | null;
  up_act_rate_kbps: number | null;
  dw_act_rate_kbps: number | null;
};

type SeltRateRow = {
  snr_margin_db: number;
  up_kbps: number;
  dw_kbps: number;
};

type SeltResult = {
  id: number;
  job_id: string | null;
  line_length_m: number | null;
  line_termination: string | null;
  line_gauge: string | null;
  updated_time: string | null;
  snr_rate_table: SeltRateRow[];
  raw_output: string;
  started_by: string;
  completed_at: string;
  modem_state: ModemState | null;
  interpretation: SeltInterpretation | null;
};

type ModemState = "connected" | "disconnected" | "unknown";

const MODEM_STATE_LABEL: Record<ModemState, string> = {
  disconnected: "modem off",
  connected: "modem connected",
  unknown: "modem state unknown",
};

/** Plain-English SELT reading from the API
 *  (chassis_shared.vdsl_selt_interpret). */
type SeltInterpretation = {
  resolution: "physical" | "profile" | "none" | "inconclusive";
  resolution_title: string;
  summary: string;
  reliability: "reliable" | "questionable" | "unreliable";
  findings: { level: "info" | "warn" | "fault"; title: string; detail: string }[];
  actions: string[];
  based_on: string[];
};

const RESOLUTION_LABEL: Record<SeltInterpretation["resolution"], string> = {
  physical: "Physical fix",
  profile: "Profile change",
  none: "No action",
  inconclusive: "Inconclusive",
};

const RESOLUTION_TONE: Record<SeltInterpretation["resolution"], BadgeTone> = {
  physical: "danger",
  profile: "warning",
  none: "success",
  inconclusive: "neutral",
};

const FINDING_DOT: Record<SeltInterpretation["findings"][number]["level"], string> = {
  fault: "bg-red-400",
  warn: "bg-amber-400",
  info: "bg-slate-500",
};

/** What the SELT found and whether the fix is physical or a profile
 *  change — cross-checked against the live line + error history. */
function SeltInterpretationCard({ i }: { i: SeltInterpretation }) {
  return (
    <div className="mt-2 flex flex-col gap-2 rounded border border-slate-800 bg-slate-900/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={RESOLUTION_TONE[i.resolution]}>
          {RESOLUTION_LABEL[i.resolution]}
        </Badge>
        <span className="text-sm font-medium text-slate-100">
          {i.resolution_title}
        </span>
        {i.reliability !== "reliable" && (
          <span className="text-[11px] text-amber-300">
            SELT reading {i.reliability}
          </span>
        )}
      </div>
      <p className="text-slate-300">{i.summary}</p>
      {i.actions.length > 0 && (
        <div>
          <h4 className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">
            Recommended next steps
          </h4>
          <ol className="ml-4 list-decimal space-y-1 text-slate-300">
            {i.actions.map((a, n) => (
              <li key={n}>{a}</li>
            ))}
          </ol>
        </div>
      )}
      <div>
        <h4 className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">
          What the test found
        </h4>
        <ul className="space-y-1.5">
          {i.findings.map((f, n) => (
            <li key={n} className="flex gap-2">
              <span
                className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${FINDING_DOT[f.level]}`}
                aria-hidden
              />
              <span>
                <span className="text-slate-200">{f.title}.</span>{" "}
                <span className="text-slate-400">{f.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
      <p className="text-[10px] text-slate-500">
        Based on: {i.based_on.join(", ")}
      </p>
    </div>
  );
}

type ErrorCounters = {
  counters: Record<string, unknown>;
  captured_at: string;
  source: string | null;
};

/** Daily counter increase (Australia/Sydney day). ``resets`` = snapshots
 * where the counters went down (port re-activated / retrained). */
type DailyDelta = {
  day: string;
  fec_s: number | null;
  es: number | null;
  ses: number | null;
  uas: number | null;
  los_failures: number | null;
  lof_failures: number | null;
  lpr_failures: number | null;
  lom_failures: number | null;
  inm_total: number | null;
  resets: number;
  samples: number;
};

type Detail = {
  port: VdslPortRow;
  line_speed_history: SamplePoint[];
  selt_results: SeltResult[];
  error_counters: ErrorCounters | null;
  error_counter_daily: DailyDelta[];
  history_days: number;
};

const HISTORY_WINDOWS = [7, 14, 30] as const;

export function VdslPortDetailPage() {
  const { chassisId, slot, port } = useParams<{
    chassisId: string;
    slot: string;
    port: string;
  }>();
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const { confirm, confirmDialog } = useConfirm();
  const [days, setDays] = useState<number>(7);
  // Asked before every SELT: a connected modem can distort SELT's
  // length/termination, and the interpreter weighs the result by it.
  const [modemState, setModemState] = useState<ModemState>("unknown");

  const detail = useQuery<Detail>({
    queryKey: ["v2", "vdsl-port-detail", chassisId, slot, port, days],
    queryFn: async () => {
      const res = await api.get<Detail>(
        `/chassis/${chassisId}/vdsl-ports/${slot}/${port}`,
        { params: { days } },
      );
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const submitSelt = useMutation({
    mutationFn: async () => {
      // The server still gates on this confirm string — sent
      // programmatically; the operator gate is the two-click dialog.
      const expected = `selt 0/${slot}/${port}`;
      const res = await api.post<{ job_id: string }>(
        `/chassis/${chassisId}/vdsl-ports/${slot}/${port}/selt`,
        { confirm: expected, modem_state: modemState },
      );
      return res.data;
    },
    onSuccess: ({ job_id }) => {
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: job_id, kind: "selt_test" },
        }),
      );
      toast.push("SELT queued", "success");
      // Go back to chassis page so the tracker is visible.
      nav(`/chassis/${chassisId}#tab=inventory`);
    },
    onError: (err: Error) => toast.push(err.message ?? "SELT failed", "error"),
  });

  const swap = useMutation({
    mutationFn: async (templateIndex: number) => {
      const expected = `swap 0/${slot}/${port} -> tmpl-${templateIndex}`;
      const res = await api.post<{ job_id: string }>(
        `/chassis/${chassisId}/vdsl-ports/${slot}/${port}/swap-profile`,
        { template_index: templateIndex, confirm: expected },
      );
      return res.data;
    },
    onSuccess: ({ job_id }) => {
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: job_id, kind: "swap_line_profile" },
        }),
      );
      toast.push("Swap queued", "success");
      qc.invalidateQueries({
        queryKey: ["v2", "vdsl-port-detail", chassisId, slot, port],
      });
      nav(`/chassis/${chassisId}#tab=inventory`);
    },
    onError: (err: Error) =>
      toast.push(err.message ?? "Swap failed", "error"),
  });

  if (detail.isLoading) {
    return <p className="text-sm text-slate-500">Loading…</p>;
  }
  if (!detail.data) {
    return (
      <p className="rounded border border-red-800 bg-red-900/40 p-3 text-sm text-red-200">
        Port not in inventory.
      </p>
    );
  }
  const d = detail.data;
  const p = d.port;
  return (
    <section className="flex flex-col gap-3">
      <Link
        to={`/chassis/${chassisId}#tab=inventory`}
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
      >
        <ChevronLeft className="h-3 w-3" />
        Back to chassis
      </Link>
      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-lg font-semibold text-slate-100">
          VDSL port{" "}
          <CopyableCell value={`${p.frame}/${p.slot}/${p.port}`} />
        </h1>
        <HelpButton topic="vdsl-port" />
        <Badge
          tone={
            p.line_state === "Activated"
              ? "success"
              : p.line_state === "Activating"
              ? "info"
              : "neutral"
          }
        >
          {p.line_state ?? "?"}
        </Badge>
        <div className="ml-auto flex items-center gap-2">
          <label
            className="flex items-center gap-1 text-xs text-slate-400"
            title="A connected modem can distort SELT's length and termination readings. Unplug it from the wall socket for the most reliable result."
          >
            Modem during SELT
            <select
              value={modemState}
              onChange={(e) => setModemState(e.target.value as ModemState)}
              className="rounded border border-slate-700 bg-slate-950 px-1.5 py-1 text-xs text-slate-100"
            >
              <option value="unknown">Don&apos;t know</option>
              <option value="disconnected">Disconnected</option>
              <option value="connected">Connected</option>
            </select>
          </label>
          <button
            type="button"
            onClick={async () => {
              const ok = await confirm({
                title: "Run SELT",
                summary:
                  `selt 0/${slot}/${port}\nModem: ${MODEM_STATE_LABEL[modemState]}\n\nDrops the customer line briefly while the test runs.` +
                  (modemState === "disconnected"
                    ? ""
                    : "\n\nFor the most reliable result, ask for the modem to be unplugged from the wall socket first."),
                confirmLabel: "Run SELT",
              });
              if (!ok) return;
              submitSelt.mutate();
            }}
            disabled={submitSelt.isPending}
            className="rounded border border-amber-700 bg-amber-900/30 px-3 py-1 text-xs text-amber-200 hover:bg-amber-900/50 disabled:opacity-50"
          >
            Run SELT (destructive)
          </button>
          <button
            type="button"
            onClick={async () => {
              const v = window.prompt(
                "Template index to swap to (integer):",
                String(p.template_index ?? 1),
              );
              if (!v) return;
              const n = parseInt(v, 10);
              if (!Number.isFinite(n)) return;
              const ok = await confirm({
                title: "Swap line profile",
                summary: `swap 0/${slot}/${port} -> tmpl-${n}\n\nDrops the line briefly while the profile swaps.`,
                confirmLabel: "Swap profile",
              });
              if (!ok) return;
              swap.mutate(n);
            }}
            disabled={swap.isPending}
            className="rounded border border-red-800 bg-red-900/30 px-3 py-1 text-xs text-red-200 hover:bg-red-900/50 disabled:opacity-50"
          >
            Swap line profile (destructive)
          </button>
        </div>
      </header>

      {/* Inventory */}
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">Inventory</h2>
        <dl className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
          <dt className="text-slate-500">Template</dt>
          <dd className="font-mono">{p.template_index ?? "—"}</dd>
          <dt className="text-slate-500">Loopback</dt>
          <dd className="font-mono">{p.loopback ?? "—"}</dd>
          <dt className="text-slate-500">Last MAC</dt>
          <dd>
            <CopyableCell value={p.last_seen_mac} />
          </dd>
          <dt className="text-slate-500">Device</dt>
          <dd>
            <CopyableCell value={p.last_seen_device} />
          </dd>
          <dt className="text-slate-500">Observed</dt>
          <dd className="font-mono text-[10px] text-slate-500">
            <LocalTime iso={p.observed_at} />
          </dd>
          <dt className="text-slate-500">Missing scans</dt>
          <dd className="font-mono">{p.missing_scans}</dd>
        </dl>
        {p.service_ports.length > 0 && (
          <>
            <h3 className="mt-3 mb-1 text-[10px] uppercase tracking-wide text-slate-500">
              Service ports ({p.service_ports.length})
            </h3>
            <ul className="flex flex-col gap-0.5 font-mono text-[11px]">
              {p.service_ports.map((sp) => (
                <li key={sp.service_port_id} className="text-slate-400">
                  sp-{sp.service_port_id} · vlan {sp.vlan} · uvlan{" "}
                  {sp.user_vlan} · {sp.tag_transform}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {/* History window picker — drives line history + counter deltas */}
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <span>History window</span>
        {HISTORY_WINDOWS.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => setDays(w)}
            aria-pressed={days === w}
            className={
              days === w
                ? "rounded border border-sky-700 bg-sky-900/40 px-2 py-0.5 text-sky-200"
                : "rounded border border-slate-700 px-2 py-0.5 hover:bg-slate-800"
            }
          >
            {w}d
          </button>
        ))}
      </div>

      {/* Line-speed chart — actual rates (Mbps) */}
      {d.line_speed_history.length > 1 && (
        <section className="rounded-lg bg-slate-900 shadow-border p-4">
          <h2 className="mb-2 text-sm font-medium text-slate-200">
            Actual rate ({d.line_speed_history.length} samples, last{" "}
            {d.history_days}d)
          </h2>
          <LineSpeedChart samples={d.line_speed_history} />
        </section>
      )}

      {/* SNR margin + attenuation charts */}
      {d.line_speed_history.length > 1 && (
        <div className="grid gap-3 lg:grid-cols-2">
          <section className="rounded-lg bg-slate-900 shadow-border p-4">
            <h2 className="mb-2 text-sm font-medium text-slate-200">
              SNR margin (dB)
            </h2>
            <UpDownChart
              samples={d.line_speed_history}
              upKey="up_snr_margin_db"
              dwKey="dw_snr_margin_db"
              unit="dB"
            />
          </section>
          <section className="rounded-lg bg-slate-900 shadow-border p-4">
            <h2 className="mb-2 text-sm font-medium text-slate-200">
              Attenuation (dB)
            </h2>
            <UpDownChart
              samples={d.line_speed_history}
              upKey="up_atten_db"
              dwKey="dw_atten_db"
              unit="dB"
            />
          </section>
        </div>
      )}

      {/* Daily error-counter deltas */}
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-1 text-sm font-medium text-slate-200">
          Daily errors (last {d.history_days}d)
        </h2>
        <VdslErrorHealthPanel
          health={d.port.error_health}
          errors={d.port.errors_7d}
          at={d.port.error_health_at}
        />
        <p className="mb-2 text-[10px] text-slate-500">
          Increase per day between counter snapshots (daily sweep + line
          refreshes). A drop in the counters means the port re-trained;
          those days count as a reset.
        </p>
        {d.error_counter_daily.length === 0 ? (
          <p className="text-xs text-slate-500">
            Needs at least two counter snapshots. The daily sweep collects
            them automatically; <em>Refresh line</em> adds one on demand.
          </p>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            <div>
              <h3 className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
                ES / SES / UAS (seconds)
              </h3>
              <DailyBarChart
                rows={d.error_counter_daily}
                series={[
                  { key: "es", label: "ES", color: "#fbbf24" },
                  { key: "ses", label: "SES", color: "#f87171" },
                  { key: "uas", label: "UAS", color: "#a78bfa" },
                ]}
              />
            </div>
            <div>
              <h3 className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
                Retrains
              </h3>
              <DailyBarChart
                rows={d.error_counter_daily}
                series={[
                  { key: "los_failures", label: "LOS failures", color: "#60a5fa" },
                  { key: "resets", label: "Counter resets", color: "#f472b6" },
                ]}
              />
            </div>
          </div>
        )}
      </section>

      {/* Line-speed history */}
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">
          Line-speed history ({d.line_speed_history.length})
        </h2>
        {d.line_speed_history.length === 0 ? (
          <p className="text-xs text-slate-500">
            No samples yet. Run <em>Collect line speed</em> from the
            chassis page Run-a-job dropdown.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">When</th>
                <th className="px-2 py-1 text-right">Up act / max</th>
                <th className="px-2 py-1 text-right">Dw act / max</th>
                <th className="px-2 py-1 text-right">SNR u / d</th>
                <th className="px-2 py-1 text-right">Atten u / d</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {d.line_speed_history.slice(-50).reverse().map((s, i) => (
                <tr key={i}>
                  <td className="px-2 py-1 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={s.observed_at} />
                  </td>
                  <td className="px-2 py-1 text-right font-mono">
                    {fmtKbps(s.up_act_rate_kbps)} / {fmtKbps(s.up_max_rate_kbps)}
                  </td>
                  <td className="px-2 py-1 text-right font-mono">
                    {fmtKbps(s.dw_act_rate_kbps)} / {fmtKbps(s.dw_max_rate_kbps)}
                  </td>
                  <td className="px-2 py-1 text-right font-mono">
                    {s.up_snr_margin_db?.toFixed(1) ?? "—"} /{" "}
                    {s.dw_snr_margin_db?.toFixed(1) ?? "—"}
                  </td>
                  <td className="px-2 py-1 text-right font-mono">
                    {s.up_atten_db?.toFixed(1) ?? "—"} /{" "}
                    {s.dw_atten_db?.toFixed(1) ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* SELT history */}
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">
          SELT history ({d.selt_results.length})
        </h2>
        {d.selt_results.length === 0 ? (
          <p className="text-xs text-slate-500">
            No SELT runs recorded. Use the destructive button above to run one.
          </p>
        ) : (
          <ul className="flex flex-col gap-2 text-xs">
            {d.selt_results.map((s, idx) => (
              <li
                key={s.id}
                className="rounded border border-slate-800 bg-slate-950/40 p-2"
              >
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className="font-mono text-[10px] text-slate-500">
                    <LocalTime iso={s.completed_at} />
                  </span>
                  <Badge
                    tone={
                      s.line_termination === "open"
                        ? "success"
                        : "danger"
                    }
                  >
                    {s.line_termination ?? "?"}
                  </Badge>
                  {s.line_length_m != null && (
                    <span className="text-slate-400">
                      length {s.line_length_m.toFixed(1)} m
                    </span>
                  )}
                  {s.line_gauge && (
                    <span className="text-slate-500">{s.line_gauge}</span>
                  )}
                  <span className="text-slate-500">by {s.started_by}</span>
                  {s.modem_state && (
                    <span className="text-slate-500">
                      {MODEM_STATE_LABEL[s.modem_state]}
                    </span>
                  )}
                  {s.interpretation && (
                    <Badge tone={RESOLUTION_TONE[s.interpretation.resolution]}>
                      {RESOLUTION_LABEL[s.interpretation.resolution]}
                    </Badge>
                  )}
                </div>
                {s.interpretation &&
                  (idx === 0 ? (
                    <SeltInterpretationCard i={s.interpretation} />
                  ) : (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-slate-400">
                        Interpretation
                      </summary>
                      <SeltInterpretationCard i={s.interpretation} />
                    </details>
                  ))}
                {s.snr_rate_table.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-slate-400">
                      SNR / rate table ({s.snr_rate_table.length} rows)
                    </summary>
                    <table className="mt-1 w-full font-mono text-[10px]">
                      <thead className="text-left text-slate-500">
                        <tr>
                          <th className="px-1">SNR dB</th>
                          <th className="px-1 text-right">Up kbps</th>
                          <th className="px-1 text-right">Dw kbps</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.snr_rate_table.map((r, i) => (
                          <tr key={i} className="text-slate-300">
                            <td className="px-1">{r.snr_margin_db}</td>
                            <td className="px-1 text-right">
                              {r.up_kbps.toLocaleString()}
                            </td>
                            <td className="px-1 text-right">
                              {r.dw_kbps.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Error counters */}
      {d.error_counters && (
        <section className="rounded-lg bg-slate-900 shadow-border p-4">
          <h2 className="mb-2 text-sm font-medium text-slate-200">
            Error counters
          </h2>
          <p className="mb-2 text-[10px] text-slate-500">
            captured <LocalTime iso={d.error_counters.captured_at} />
            {d.error_counters.source && (
              <> · via {d.error_counters.source.replace(/_/g, " ")}</>
            )}{" "}
            · cumulative totals
          </p>
          <CounterTable counters={d.error_counters.counters} />
        </section>
      )}
      {confirmDialog}
    </section>
  );
}

function fmtKbps(v: number | null): string {
  if (v == null) return "—";
  if (v >= 1000) return `${(v / 1000).toFixed(1)}M`;
  return `${v}k`;
}

function LineSpeedChart({ samples }: { samples: SamplePoint[] }) {
  // Convert kbps → Mbps for display and add a friendly short
  // timestamp ("MM-DD HH:mm") so the X axis isn't an unreadable ISO
  // string. Recharts wants numeric Y values — null samples are
  // omitted from each line via ``connectNulls={false}``.
  const data = samples.map((s) => ({
    ts: formatLocalShort(s.observed_at),
    up: s.up_act_rate_kbps != null ? s.up_act_rate_kbps / 1000 : null,
    dw: s.dw_act_rate_kbps != null ? s.dw_act_rate_kbps / 1000 : null,
  }));
  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
          <XAxis
            dataKey="ts"
            tick={{ fill: "#64748b", fontSize: 10 }}
            stroke="#334155"
          />
          <YAxis
            tick={{ fill: "#64748b", fontSize: 10 }}
            stroke="#334155"
            unit=" Mbps"
            width={80}
          />
          <Tooltip
            contentStyle={{
              background: "#0f172a",
              border: "1px solid #334155",
              fontSize: 11,
            }}
            labelStyle={{ color: "#94a3b8" }}
            formatter={(value, name) => [
              typeof value === "number" ? `${value.toFixed(1)} Mbps` : "—",
              name === "up" ? "Up" : "Dw",
            ]}
          />
          <Line
            type="monotone"
            dataKey="up"
            stroke="#34d399"
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="dw"
            stroke="#60a5fa"
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

type NumericSampleKey =
  | "up_snr_margin_db"
  | "dw_snr_margin_db"
  | "up_atten_db"
  | "dw_atten_db";

/** Up/down pair over time (SNR margin, attenuation) — same look as
 * LineSpeedChart: green = upstream, blue = downstream. */
function UpDownChart({
  samples,
  upKey,
  dwKey,
  unit,
}: {
  samples: SamplePoint[];
  upKey: NumericSampleKey;
  dwKey: NumericSampleKey;
  unit: string;
}) {
  const data = samples.map((s) => ({
    ts: formatLocalShort(s.observed_at),
    up: s[upKey],
    dw: s[dwKey],
  }));
  return (
    <div className="h-48 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
          <XAxis
            dataKey="ts"
            tick={{ fill: "#64748b", fontSize: 10 }}
            stroke="#334155"
          />
          <YAxis
            tick={{ fill: "#64748b", fontSize: 10 }}
            stroke="#334155"
            unit={` ${unit}`}
            width={60}
          />
          <Tooltip
            contentStyle={{
              background: "#0f172a",
              border: "1px solid #334155",
              fontSize: 11,
            }}
            labelStyle={{ color: "#94a3b8" }}
            formatter={(value, name) => [
              typeof value === "number" ? `${value.toFixed(1)} ${unit}` : "—",
              name === "up" ? "Up" : "Dw",
            ]}
          />
          <Legend
            wrapperStyle={{ fontSize: 10 }}
            formatter={(name) => (name === "up" ? "Up" : "Down")}
          />
          <Line
            type="monotone"
            dataKey="up"
            stroke="#34d399"
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="dw"
            stroke="#60a5fa"
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

type DailySeries = {
  key: "es" | "ses" | "uas" | "los_failures" | "resets";
  label: string;
  color: string;
};

/** Grouped bars per local day for the daily counter deltas. */
function DailyBarChart({
  rows,
  series,
}: {
  rows: DailyDelta[];
  series: DailySeries[];
}) {
  const labels = Object.fromEntries(series.map((s) => [s.key, s.label]));
  const data = rows.map((r) => {
    const point: Record<string, string | number | null> = { day: r.day.slice(5) };
    for (const s of series) point[s.key] = r[s.key];
    return point;
  });
  return (
    <div className="h-48 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
          <XAxis
            dataKey="day"
            tick={{ fill: "#64748b", fontSize: 10 }}
            stroke="#334155"
          />
          <YAxis
            tick={{ fill: "#64748b", fontSize: 10 }}
            stroke="#334155"
            width={60}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              background: "#0f172a",
              border: "1px solid #334155",
              fontSize: 11,
            }}
            labelStyle={{ color: "#94a3b8" }}
            cursor={{ fill: "#1e293b" }}
            formatter={(value, name) => [
              typeof value === "number" ? value.toLocaleString() : "—",
              labels[String(name)] ?? String(name),
            ]}
          />
          <Legend
            wrapperStyle={{ fontSize: 10 }}
            formatter={(name) => labels[String(name)] ?? String(name)}
          />
          {series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              fill={s.color}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Grouped, labelled counter snapshot (replaces the raw JSON dump). */
function CounterTable({ counters }: { counters: Record<string, unknown> }) {
  const groups = groupCounters(counters);
  if (groups.length === 0) {
    return <p className="text-xs text-slate-500">No counters parsed.</p>;
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {groups.map((g) => (
        <table key={g.key} className="w-full self-start text-xs">
          <caption className="mb-1 text-left text-[10px] uppercase tracking-wide text-slate-500">
            {g.title}
          </caption>
          <tbody className="divide-y divide-slate-800/40 text-slate-300">
            {g.rows.map((r) => (
              <tr key={r.key}>
                <td className="py-0.5 pr-2 text-slate-400">{r.label}</td>
                <td className="py-0.5 text-right font-mono tabular-nums">
                  {formatCounterValue(r.value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </div>
  );
}
